import React, { useRef } from 'react'
import styled from 'styled-components'
import { scrubText } from 'helpers/inputCleaners'

import TextInput from 'components/TextInput'
import StatesDropdown from 'components/StatesDropdown'

import { screenSize } from 'styles/shared'

type errorProps = {
  firstName: string
  lastName: string
  address: string
  city: string
  state: string
  zip: string
  email: string
}

type Props = {
  firstName?: string
  lastName?: string
  address?: string
  address2?: string
  city?: string
  state?: string
  zip?: string
  email?: string
  isDso?: boolean
  errors: errorProps
  onChangeCallback: (updatedAttribute) => void
}

const PaywallAccountFields = ({
  firstName = '',
  lastName = '',
  address = '',
  address2 = '',
  city = '',
  state = '',
  zip = '',
  email = '',
  isDso = false,
  errors,
  onChangeCallback
}: Props) => {
  const firstNameInputRef = useRef<HTMLInputElement>(null)

  return (
    <>
      <S.Title>Billing Address</S.Title>

      <S.Fields>
        <S.Row>
          <S.TextInput
            label={`Name${!isDso ? ' (as it appears on card)' : ''}`}
            placeholder="First"
            onChange={event => onChangeCallback({ firstName: scrubText(event.target.value) })}
            value={firstName}
            errorMessage={errors.firstName}
            ref={firstNameInputRef}
          />
          <S.TextInput
            label=""
            placeholder="Last"
            onChange={event => onChangeCallback({ lastName: scrubText(event.target.value) })}
            value={lastName}
            errorMessage={errors.lastName}
            aria-label="Last Name"
          />
        </S.Row>

        <S.Row>
          <S.TextInput
            label="Address"
            placeholder="Street"
            onChange={event => onChangeCallback({ address: scrubText(event.target.value) })}
            value={address}
            maxLength={255}
            errorMessage={errors.address}
            disabled={isDso}
          />
          <S.TextInput
            label="Address 2 (optional)"
            placeholder="Unit, floor, etc."
            onChange={event => onChangeCallback({ address2: scrubText(event.target.value) })}
            value={address2}
            maxLength={255}
            disabled={isDso}
          />
        </S.Row>

        <S.AddressRow>
          <S.TextInput
            label="City"
            onChange={event => onChangeCallback({ city: scrubText(event.target.value) })}
            value={city}
            errorMessage={errors.city}
            disabled={isDso}
          />
          <S.StatesDropdown
            onChange={event => onChangeCallback({ state: scrubText(event.target.value) })}
            value={state}
            errorMessage={errors.state}
            disabled={isDso}
          />
          <S.ZipCodeInput
            label="Zip Code"
            onChange={event => onChangeCallback({ zip: scrubText(event.target.value) })}
            value={zip}
            maxLength={10}
            errorMessage={errors.zip}
            disabled={isDso}
          />
        </S.AddressRow>

        <S.SingleFieldRow>
          <S.EmailInput
            label="Email"
            onChange={event => onChangeCallback({ email: scrubText(event.target.value) })}
            value={email}
            errorMessage={errors.email}
          />
        </S.SingleFieldRow>
      </S.Fields>
    </>
  )
}

export default PaywallAccountFields

const baseRowStyle = `
  display: grid;
  align-items: end;
  gap: 10px;

  @media ${screenSize.small} {
    grid-template-columns: 1fr;
    gap: 0;
    margin-bottom: 15px;
  }
`

const S = {
  Title: styled.h2`
    text-transform: none;
    margin-top: 15px;
  `,
  Fields: styled.div`
    padding: 10px 0;

    @media ${screenSize.small} {
      padding: 0;
    }
  `,
  Row: styled.div`
    ${baseRowStyle}

    grid-template-columns: 1fr 1fr;
  `,
  AddressRow: styled.div`
    ${baseRowStyle}

    grid-template-columns: 1fr .5fr .5fr;
  `,
  SingleFieldRow: styled.div`
    ${baseRowStyle}

    grid-template-columns: 1fr;
  `,
  TextInput: styled(TextInput)`
    max-width: 400px;

    @media ${screenSize.small} {
      max-width: 100%;
    }
  `,
  StatesDropdown: styled(StatesDropdown)`
    max-width: 150px;
    margin-left: 8px;

    @media ${screenSize.small} {
      max-width: 100%;
      margin-left: 0;
    }
  `,
  ZipCodeInput: styled(TextInput)`
    max-width: 100px;

    @media ${screenSize.small} {
      max-width: 100%;
    }
  `,
  EmailInput: styled(TextInput)`
    max-width: 865px;
  `
}
